/**
 * Where a notification opens in the SPA (web plan W0.17): its deep link, or
 * failing that an entity id in its `data`, as an in-app path — or `null`, and
 * then nothing is navigated.
 *
 * Links the backend sends or has sent (`Notification.link`, push `data.link`;
 * backend B0.8):
 *   promostork://promotions/64ab…              app scheme, plural (B0.8's consumer links)
 *   promostork://promotion/64ab…               app scheme, singular
 *   https://promostork.com/promotion/64ab…     a page of this site (www. too)
 *   https://promostork.com/pk/en/promotion/unze-london-up-to-75-off-sep-2026
 *                                              a slug URL, once the backend sends them (B1.4, web W2)
 *   /shops/64ab…                               a bare site path
 *
 * Each is reduced to its path segments, a launched `/{cc}/{lang}` prefix is
 * read off, and only a page the SPA has is accepted:
 *   [{cc}/{lang}/]{promotion|brand|store|shop}[s]/{id or slug}
 *                                                → [/{cc}/{lang}]/{type}/{ref}, prefix kept:
 *       the SPA has the entity routes under every launched locale (W0.16), and
 *       the page itself moves to its `urlPath`. The plural `/promotions/{id}`
 *       is the SPA's 404, so every plural opens the singular route.
 *   brands, promotions, shops                    → that catalog list
 *   notification[s], favorites, nearby           → that page
 *     (the lists and nearby under `/{cc}/{lang}` once the URL move is live,
 *     web plan W2.1; un-prefixed before it, and the others always)
 *   {cc}/{lang}/sales, {cc}/{lang}/sale/{event}  → the sale hubs, prefix kept (they exist only under it)
 *
 * Everything else is `null`: another site (the Dashboard's admin links
 * included), plain http on a host other than this tab's, `//host`, other
 * schemes (javascript:, mailto:, data:, …), app routes the site has no page
 * for (invites, memberships), and malformed ids or slugs. The `data.*Id`
 * fallback takes ObjectIds only.
 *
 * public/firebase-messaging-sw.js repeats these rules in plain JS for push
 * clicks (it is served verbatim and cannot import this module); change both
 * together — notificationLink.test.ts runs both against one table.
 */
import { DEFAULT_LOCALE, parseLocale } from '@/seo/locales';
import { SITE_URL, catalogPath, localePath } from '@/seo/urls';

/** Entity pages by their first segment, either spelling → the SPA's singular route. */
const ENTITY_ROUTES: ReadonlyMap<string, string> = new Map([
  ['promotion', '/promotion'],
  ['promotions', '/promotion'],
  ['brand', '/brand'],
  ['brands', '/brand'],
  ['store', '/store'],
  ['stores', '/store'],
  ['shop', '/shop'],
  ['shops', '/shop'],
]);

/** Pages without a parameter, by their segment. (Maps, so `constructor` is no route.) */
const PAGE_ROUTES: ReadonlyMap<string, string> = new Map([
  ['brands', '/brands'],
  ['promotions', '/promotions'],
  ['shops', '/shops'],
  ['notification', '/notifications'],
  ['notifications', '/notifications'],
  ['favorites', '/favorites'],
  ['nearby', '/nearby'],
]);

/**
 * The catalog pages among them: under the link's locale (or the default one)
 * once the URL move is live (`catalogPath`, web plan W2.1), un-prefixed
 * before it. The service worker keeps opening the un-prefixed path, which the
 * server then 301s (a full page load either way).
 */
const CATALOG_PAGES: ReadonlySet<string> = new Set(['/brands', '/promotions', '/shops', '/nearby']);

/** Pages that ignore anything after their segment (`promostork://notifications/{id}`), as before W0.17. */
const LENIENT_PAGES: ReadonlySet<string> = new Set(['notification', 'notifications', 'favorites', 'nearby']);

const OBJECT_ID_RE = /^[a-f\d]{24}$/i;

/**
 * A URL slug as the backend writes it (B1.2 `normaliseSlugKey`): lowercase
 * ASCII words joined by single hyphens, at most 120 characters. Matched in
 * lower case, which is how the backend resolves it.
 */
const SLUG_RE = /^[a-z\d]+(?:-[a-z\d]+)*$/;
const SLUG_MAX_LENGTH = 120;

function slugOf(segment: string | undefined): string | null {
  const slug = (segment ?? '').toLowerCase();
  return slug.length <= SLUG_MAX_LENGTH && SLUG_RE.test(slug) ? slug : null;
}

/**
 * Never an entity's slug: the backend's reserved words (B1.2 RESERVED_SLUGS;
 * `/brand/details/{id}` was the old staff link) and a missing id written out
 * (`promostork://promotions/undefined`), so the `data.*Id` fallback still gets
 * its turn.
 */
const NOT_A_SLUG: ReadonlySet<string> = new Set([
  'new', 'edit', 'details', 'search', 'nearby', 'all', 'admin', 'api',
  'sitemap', 'sitemaps', 'login', 'page', 'undefined', 'null',
]);

/** An entity's `{ref}`: its ObjectId as written, or its slug. */
function entityRef(segment: string | undefined): string | null {
  if (segment && OBJECT_ID_RE.test(segment)) return segment;
  const slug = slugOf(segment);
  return slug && !NOT_A_SLUG.has(slug) ? slug : null;
}

/** The route for a link's path segments, or null. */
function routeFor(segments: readonly string[]): string | null {
  let rest = segments;
  let prefix = '';
  const locale = segments.length >= 2 ? parseLocale(segments[0], segments[1]) : null;
  if (locale) {
    prefix = localePath(locale);
    rest = segments.slice(2);
  }
  const head = (rest[0] ?? '').toLowerCase();
  const ref = rest[1];

  if (prefix && head === 'sales' && rest.length === 1) return `${prefix}/sales`;
  if (prefix && head === 'sale' && rest.length === 2) {
    const event = slugOf(ref);
    return event ? `${prefix}/sale/${event}` : null;
  }
  const entity = ENTITY_ROUTES.get(head);
  if (entity && rest.length === 2) {
    const id = entityRef(ref);
    return id ? `${prefix}${entity}/${id}` : null;
  }
  const page = PAGE_ROUTES.get(head);
  if (page && (rest.length === 1 || LENIENT_PAGES.has(head))) {
    return CATALOG_PAGES.has(page) ? catalogPath(locale ?? DEFAULT_LOCALE, page) : page;
  }
  return null;
}

/** A path's segments, without its query or fragment. */
function pathSegments(path: string): string[] {
  return path.split(/[?#]/, 1)[0].split('/').filter(Boolean);
}

/** `www.promostork.com` is the same site as `promostork.com`. */
const siteHost = (host: string): string => host.toLowerCase().replace(/^www\./, '');

/** This tab's origin (the dev server, a staging host), or null outside a browser. */
function currentOrigin(): string | null {
  const origin = typeof window === 'undefined' ? null : window.location?.origin;
  return origin && origin !== 'null' ? origin : null;
}

/**
 * A URL of this site: exactly this tab's origin, whatever its scheme (plain
 * http on the dev server), or https on SITE_URL's host or this tab's, with or
 * without `www.`.
 */
function isOwnSite(url: URL): boolean {
  const here = currentOrigin();
  if (here && url.origin === here) return true;
  if (url.protocol !== 'https:') return false;
  const host = siteHost(url.host);
  return [SITE_URL, here].some((origin) => {
    if (!origin) return false;
    try {
      const site = new URL(origin);
      return site.protocol === 'https:' && siteHost(site.host) === host;
    } catch {
      return false;
    }
  });
}

/** `promostork://promotions/64ab…`: the "host" is the first segment. */
const APP_LINK_RE = /^promostork:\/\/([^?#]*)/i;

/** The path segments a link names on this site, or null for anything else. */
function linkSegments(link: string): string[] | null {
  const raw = link.trim();
  const app = APP_LINK_RE.exec(raw);
  if (app) return pathSegments(app[1]);
  // A bare site path. `//host/…` (and `/\host`, which browsers read the same
  // way) is another site.
  if (raw.startsWith('/')) return /^\/[/\\]/.test(raw) ? null : pathSegments(raw);
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if ((url.protocol === 'https:' || url.protocol === 'http:') && isOwnSite(url)) {
    return pathSegments(url.pathname);
  }
  return null;
}

/** The in-app path a notification link opens, or null (see the module comment). */
export function parseNotificationLink(link?: string | null): string | null {
  if (!link || typeof link !== 'string') return null;
  const segments = linkSegments(link);
  return segments ? routeFor(segments) : null;
}

/** Fallback: derive a route from the push/realtime `data` payload. */
export function parseNotificationData(
  data?: Record<string, unknown> | string | null,
): string | null {
  if (!data) return null;
  let obj: Record<string, unknown>;
  if (typeof data === 'string') {
    try {
      obj = JSON.parse(data) as Record<string, unknown>;
    } catch {
      return null;
    }
  } else {
    obj = data;
  }
  // JSON.parse('null') and friends.
  if (!obj || typeof obj !== 'object') return null;
  if (typeof obj.link === 'string') {
    const fromLink = parseNotificationLink(obj.link);
    if (fromLink) return fromLink;
  }
  // Every push producer keeps these ids (backend B0.8); they are ObjectIds,
  // never slugs, so nothing else is accepted here.
  const pairs: Array<[string, string]> = [
    ['promotionId', '/promotion'],
    ['brandId', '/brand'],
    ['storeId', '/store'],
    ['shopId', '/shop'],
  ];
  for (const [key, route] of pairs) {
    const v = obj[key];
    if (typeof v === 'string' && OBJECT_ID_RE.test(v)) return `${route}/${v}`;
  }
  return null;
}

/** Best-effort route for a notification: link first, then data payload. */
export function notificationRoute(
  link?: string | null,
  data?: Record<string, unknown> | string | null,
): string | null {
  return parseNotificationLink(link) ?? parseNotificationData(data);
}
